import { useMemo, useState } from 'react';
import { Button, Card, Badge, Input, Modal, LoadingSkeleton } from '@nexora/design-system';
import { useCustomers } from '../master-data/useMasterData';
import {
  useContacts,
  useCreateContact,
  useDeleteContact,
} from './useContacts';

/* Diretório Global de Contatos (RF-013): lista Contact — pessoa CRM com
   cargo e engajamento — distinto de Party. Empresa resolvida no servidor. */

function scoreColor(score: number | null): 'success' | 'warning' | 'error' | 'neutral' {
  if (score === null) return 'neutral';
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'error';
}

export function ContactsDirectory(): JSX.Element {
  const { data, isLoading, error } = useContacts({ limit: 100 });
  const createContact = useCreateContact();
  const deleteContact = useDeleteContact();
  const customersQuery = useCustomers();

  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formError, setFormError] = useState('');
  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [engagement, setEngagement] = useState('');

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        (c.companyName ?? '').toLowerCase().includes(term) ||
        (c.roleTitle ?? '').toLowerCase().includes(term),
    );
  }, [data, search]);

  const customers = useMemo(() => customersQuery.data ?? [], [customersQuery.data]);
  const sugestoes = useMemo(() => {
    const termo = buscaCliente.trim().toLowerCase();
    if (termo.length < 2) return [];
    return customers
      .filter((c) => c.name.toLowerCase().includes(termo))
      .slice(0, 8);
  }, [buscaCliente, customers]);

  const submit = (): void => {
    if (!name.trim()) {
      setFormError('Nome é obrigatório.');
      return;
    }
    if (!customerId) {
      setFormError('Vincule a uma empresa (cliente).');
      return;
    }
    const score = engagement.trim() === '' ? undefined : Number(engagement);
    if (score !== undefined && (!Number.isInteger(score) || score < 0 || score > 100)) {
      setFormError('Engajamento deve ser inteiro de 0 a 100.');
      return;
    }
    setFormError('');
    createContact.mutate(
      {
        customerId,
        name: name.trim(),
        ...(roleTitle.trim() ? { roleTitle: roleTitle.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(score !== undefined ? { engagementScore: score } : {}),
      },
      {
        onSuccess: () => {
          setShowModal(false);
          setName('');
          setCustomerId('');
          setBuscaCliente('');
          setRoleTitle('');
          setEmail('');
          setPhone('');
          setEngagement('');
        },
        onError: (err) => setFormError((err as Error).message),
      },
    );
  };

  return (
    <div className="mb-lg">
      <div className="mb-md flex flex-col gap-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          {isLoading ? 'Carregando…' : `${rows.length} contato${rows.length === 1 ? '' : 's'}`}
        </p>
        <div className="flex gap-sm">
          <Input
            id="contact-search"
            placeholder="Buscar nome, empresa ou cargo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="primary" onClick={() => setShowModal(true)}>
            Novo contato
          </Button>
        </div>
      </div>

      <Card padding="0">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={4} height="40px" />
          </div>
        )}
        {!isLoading && error && (
          <p className="m-0 p-md font-body-md text-body-md text-error">
            {(error as Error).message}
          </p>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
            Nenhum contato cadastrado. Cadastre a pessoa certa para falar no cliente.
          </p>
        )}
        {!isLoading && !error && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[0.875rem]">
              <thead>
                <tr>
                  <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                    Contact Name
                  </th>
                  <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                    Company
                  </th>
                  <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                    Position
                  </th>
                  <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                    Engagement
                  </th>
                  <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                    Status
                  </th>
                  <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-right font-label-sm text-label-sm uppercase text-on-surface-variant">
                    Quick Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((contact) => (
                  <tr key={contact.id}>
                    <td className="border-b border-outline-variant px-md py-sm font-semibold text-on-surface">
                      {contact.name}
                    </td>
                    <td className="border-b border-outline-variant px-md py-sm text-on-surface-variant">
                      {contact.companyName ?? '—'}
                    </td>
                    <td className="border-b border-outline-variant px-md py-sm text-on-surface-variant">
                      {contact.roleTitle ?? '—'}
                    </td>
                    <td className="border-b border-outline-variant px-md py-sm">
                      {contact.engagementScore === null ? (
                        <span className="text-on-surface-variant">—</span>
                      ) : (
                        <Badge variant={scoreColor(contact.engagementScore)}>
                          {contact.engagementScore}
                        </Badge>
                      )}
                    </td>
                    <td className="border-b border-outline-variant px-md py-sm">
                      <Badge variant="success">ACTIVE</Badge>
                    </td>
                    <td className="border-b border-outline-variant px-md py-sm text-right">
                      {contact.email && (
                        <a
                          className="mr-sm font-label-md text-label-md text-primary hover:underline"
                          href={`mailto:${contact.email}`}
                          aria-label={`E-mail para ${contact.name}`}
                        >
                          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                            mail
                          </span>
                        </a>
                      )}
                      <button
                        type="button"
                        className="font-label-md text-label-md text-error hover:underline"
                        disabled={deleteContact.isPending}
                        onClick={() => deleteContact.mutate(contact.id)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Novo contato"
      >
        <div className="flex flex-col gap-md">
          <Input
            id="new-contact-name"
            label="Nome *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Carlos Andrade"
          />
          <div>
            <div className="field-label">Empresa (cliente) *</div>
            <Input
              id="new-contact-company"
              placeholder="Buscar cliente…"
              value={buscaCliente}
              onChange={(e) => setBuscaCliente(e.target.value)}
            />
            {sugestoes.length > 0 && (
              <ul
                className="mt-xs overflow-hidden rounded-md border border-outline-variant bg-surface-container-lowest"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                {sugestoes.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="w-full px-sm py-xs text-left font-body-md text-body-md text-on-surface hover:bg-surface-container-low"
                      onClick={() => {
                        setCustomerId(c.id);
                        setBuscaCliente(c.name);
                      }}
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Input
            id="new-contact-role"
            label="Cargo"
            value={roleTitle}
            onChange={(e) => setRoleTitle(e.target.value)}
            placeholder="Ex.: CTO"
          />
          <Input
            id="new-contact-email"
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            id="new-contact-phone"
            label="Telefone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            id="new-contact-engagement"
            label="Engajamento (0–100)"
            type="number"
            min="0"
            max="100"
            value={engagement}
            onChange={(e) => setEngagement(e.target.value)}
          />
          {formError ? (
            <p className="m-0 font-body-sm text-body-sm text-error">{formError}</p>
          ) : null}
          <div className="flex justify-end gap-sm">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={createContact.isPending}
              onClick={submit}
            >
              Salvar contato
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
